import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * Adds listeners for push notifications registration and errors.
 * Triggers the `onRegister` callback with the device token upon successful registration.
 *
 * @param {Object} options - Configuration options for adding listeners.
 * @param {Function} options.onRegister - Callback to execute with the device token.
 */
export function addListeners({ onRegister }) {
  if (onRegister && Capacitor.getPlatform() !== 'web') {
    PushNotifications.addListener('registration', (token) => {
      onRegister(token.value);
    });

    PushNotifications.addListener('registrationError', console.error);
  }
}

/**
 * Requests permission for and registers the device for push notifications if granted.
 * Handles both local and push notifications depending on platform environment.
 */
export function register() {
  if (window.env.isApp) {
    PushNotifications.checkPermissions().then(async (permStatus) => {
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        return console.debug('PushNotifications : user denied permissions');
      }

      PushNotifications.register();
    });
  }
}

/**
 * Adds a listener for receiving notifications and executes a callback upon reception.
 *
 * @param {Function} callback - Callback to execute when a notification is received.
 */
export function onReceived(callback) {
  LocalNotifications.addListener('localNotificationReceived', (notification) => {
    console.debug({ localNotificationReceived: notification });
    callback(notification);
  });

  if (window.env.isApp) {
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.debug({ pushNotificationReceived: notification });
      callback(notification);
    });
  }
}

/**
 * Adds a listener that handles the action of a user interacting with a received notification.
 *
 * @param {Function} callback - Callback to execute when the notification is interacted with.
 */
export function onActionPerformed(callback) {
  LocalNotifications.addListener('localNotificationActionPerformed', ({ notification }) => {
    callback(notification);
  });

  if (window.env.isApp) {
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      callback(notification);
    });
  }
}

/**
 * Schedules a local notification if the app is not a web application.
 *
 * @param {Object} notification - Notification configuration object.
 */
export async function schedule(notification) {
  const permsStatus = await LocalNotifications.checkPermissions();

  if (permsStatus.display === 'denied') {
    return console.debug('LocalNotifications : permissions denied');
  }

  if (permsStatus.display !== 'granted') {
    const perms = await LocalNotifications.requestPermissions();

    console.debug({ LocalNotificationsPermissions: perms });

    if (perms.display !== 'granted') {
      return console.debug('LocalNotifications : permissions not granted');
    }
  }

  // Schedule the notification
  await LocalNotifications.schedule({ notifications: [notification] });
}

/**
 * Retrieves all pending notifications that are scheduled but not yet delivered.
 *
 * @returns {Promise<Array>} - Returns an array of pending notifications.
 */
export async function getPending() {
  // Retrieve all pending notifications
  const pendingNotifications = await LocalNotifications.getPending();

  return pendingNotifications;
}

/**
 * Cancels a scheduled notification by its identifier.
 *
 * @param {string} notificationId - The identifier of the notification to cancel.
 */
export async function cancelScheduledNotification(notificationId) {
  await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
}
